// Lead-report table behaviour (Reports/Leads): client-side sort + search, row selection,
// the bulk SMS/WhatsApp composer, and the "compare against an Odyssea order list" panel.
// Everything here works on the already-rendered table — the filter panel itself is a plain GET
// form, so a filter change is a normal page load and this module simply re-initialises.

interface BlastRow { id: string; name: string; mobile: string; ok: boolean; info: string; skipped?: boolean }
interface BlastReply {
  ok: boolean; err?: string; sent?: number; failed?: number; results?: BlastRow[];
  // Preview / skip counts: who a marketing message may not reach, and why (MarketingRules).
  willSend?: number; optedOut?: number; noConsent?: number; skipped?: number;
}
interface TestReply { ok: boolean; err?: string; info?: string; body?: string }

export function initLeadReport(): void {
  const root = document.getElementById("leadReport");
  if (!root) return;

  const table = root.querySelector<HTMLTableElement>("table[data-report-table]");
  const tbody = table?.tBodies[0];
  const token = () => root.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
  const $ = <T extends HTMLElement>(sel: string) => root.querySelector<T>(sel);

  // Data rows only — the "no results" placeholder row must never sort/filter/select.
  const rows = (): HTMLTableRowElement[] =>
    tbody ? Array.from(tbody.rows).filter((r) => r.dataset.row !== undefined) : [];

  // ================= search =================
  const searchBox = $<HTMLInputElement>("[data-report-search]");
  const shownCount = $<HTMLElement>("[data-shown-count]");

  function applySearch() {
    const q = (searchBox?.value ?? "").trim().toLowerCase();
    let shown = 0;
    for (const r of rows()) {
      const hit = q === "" || r.textContent!.toLowerCase().includes(q);
      r.style.display = hit ? "" : "none";
      if (hit) shown++;
    }
    if (shownCount) shownCount.textContent = String(shown);
    refreshSelection();
  }
  searchBox?.addEventListener("input", applySearch);

  // ================= sort =================
  // A cell may carry data-v with a sort key (ISO date / raw number); otherwise its text is used.
  // Direction toggles on repeat clicks; empty values always sink to the bottom.
  let sortCol = -1;
  let sortAsc = true;

  function cellKey(row: HTMLTableRowElement, col: number): string {
    const c = row.cells[col];
    if (!c) return "";
    return (c.dataset.v ?? c.textContent ?? "").trim();
  }

  function sortBy(col: number) {
    if (!tbody) return;
    sortAsc = col === sortCol ? !sortAsc : true;
    sortCol = col;

    const data = rows();
    const numeric = data.every((r) => {
      const v = cellKey(r, col).replace(/,/g, "");
      return v === "" || !isNaN(Number(v));
    });

    data.sort((a, b) => {
      const x = cellKey(a, col);
      const y = cellKey(b, col);
      if (x === "" && y === "") return 0;
      if (x === "") return 1;   // blanks last, in both directions
      if (y === "") return -1;
      const cmp = numeric
        ? Number(x.replace(/,/g, "")) - Number(y.replace(/,/g, ""))
        : x.localeCompare(y, "he");
      return sortAsc ? cmp : -cmp;
    });
    for (const r of data) tbody.appendChild(r);

    table?.querySelectorAll<HTMLTableCellElement>("th[data-sortable]").forEach((th) => {
      th.dataset.dir = th.cellIndex === col ? (sortAsc ? "asc" : "desc") : "";
    });
  }

  // Sort on the header's real cell index — the checkbox column is not sortable, so the position
  // among sortable headers is not the column position.
  table?.querySelectorAll<HTMLTableCellElement>("th[data-sortable]").forEach((th) =>
    th.addEventListener("click", () => sortBy(th.cellIndex)));

  // ================= selection =================
  const checks = () => rows().filter((r) => r.style.display !== "none")
    .map((r) => r.querySelector<HTMLInputElement>(".rep-check"))
    .filter((c): c is HTMLInputElement => !!c);
  const selected = () => rows()
    .map((r) => r.querySelector<HTMLInputElement>(".rep-check"))
    .filter((c): c is HTMLInputElement => !!c && c.checked);

  const selCount = $<HTMLElement>("[data-sel-count]");
  function refreshSelection() {
    const n = selected().length;
    if (selCount) selCount.textContent = String(n);
    root!.querySelectorAll<HTMLButtonElement>("[data-needs-selection]").forEach((b) => { b.disabled = n === 0; });
  }

  root.addEventListener("change", (e) => {
    const el = e.target as HTMLElement;
    if (el.classList.contains("rep-check-all")) {
      const on = (el as HTMLInputElement).checked;
      checks().forEach((c) => { c.checked = on; });   // visible rows only
      refreshSelection();
    } else if (el.classList.contains("rep-check")) {
      refreshSelection();
    }
  });

  // ================= modal plumbing =================
  const show = (id: string) => { const e = document.getElementById(id); if (e) e.style.display = "flex"; };
  const hide = (id: string) => { const e = document.getElementById(id); if (e) e.style.display = "none"; };
  root.querySelectorAll<HTMLElement>("[data-close]").forEach((b) =>
    b.addEventListener("click", () => hide(b.dataset.close!)));
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") hide("blastModal"); });

  async function post<T>(url: string, data: Record<string, string | string[]>): Promise<T | null> {
    const body = new URLSearchParams();
    body.set("__RequestVerificationToken", token());
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) v.forEach((x) => body.append(k, x));
      else body.set(k, v);
    }
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) return null;
      return (await res.json()) as T;
    } catch {
      return null;
    }
  }

  // ================= bulk SMS / WhatsApp =================
  const blastOut = $<HTMLElement>("[data-blast-results]");
  const blastText = $<HTMLTextAreaElement>("[data-blast-text]");
  const testOut = $<HTMLElement>("[data-test-result]");

  const channel = () =>
    root!.querySelector<HTMLInputElement>('input[name="blastChannel"]:checked')?.value ?? "sms";
  const kind = () =>
    root!.querySelector<HTMLInputElement>('input[name="blastKind"]:checked')?.value ?? "marketing";

  root.querySelector<HTMLElement>("[data-open-blast]")?.addEventListener("click", () => {
    if (blastOut) blastOut.innerHTML = "";
    if (testOut) testOut.textContent = "";
    const n = selected().length;
    const target = $<HTMLElement>("[data-blast-target]");
    if (target) target.textContent = String(n);
    show("blastModal");
  });

  // Template picker drops a ready-made text into the box.
  $<HTMLSelectElement>("[data-blast-template]")?.addEventListener("change", (e) => {
    const v = (e.target as HTMLSelectElement).value;
    if (v && blastText) blastText.value = v;
  });

  $<HTMLButtonElement>("[data-send-test]")?.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const mobile = $<HTMLInputElement>("[data-test-mobile]")?.value ?? "";
    if (!testOut) return;
    btn.disabled = true;
    testOut.className = "mt-2 text-xs text-gray-500";
    testOut.textContent = "שולח בדיקה…";
    const first = selected()[0];
    const r = await post<TestReply>("/Reports/SendTest", {
      mobile,
      text: blastText?.value ?? "",
      channel: channel(),
      sampleId: first?.dataset.id ?? "",
    });
    btn.disabled = false;
    if (!r) { testOut.className = "mt-2 text-xs text-error-600"; testOut.textContent = "שגיאת רשת"; return; }
    if (r.ok) {
      testOut.className = "mt-2 text-xs text-success-700";
      testOut.textContent = `✔ נשלחה בדיקה אל ${mobile}. הטקסט שנשלח: ${r.body ?? ""}`;
    } else {
      testOut.className = "mt-2 text-xs text-error-600";
      testOut.textContent = `✖ ${r.err ?? r.info ?? "השליחה נכשלה"}`;
    }
  });

  $<HTMLButtonElement>("[data-send-blast]")?.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const sel = selected();
    if (sel.length === 0 || !blastOut) return;
    const payload = {
      ids: sel.map((c) => c.dataset.id ?? ""),
      mobiles: sel.map((c) => c.dataset.mobile ?? ""),
      text: blastText?.value ?? "",
      channel: channel(),
      kind: kind(),
    };

    // The server says first who the message may reach; the person confirms with those numbers.
    btn.disabled = true;
    const p = await post<BlastReply>("/Reports/SendBulk", { ...payload, preview: "true" });
    btn.disabled = false;
    if (!p) { blastOut.innerHTML = '<p class="text-sm text-error-600">שגיאת רשת</p>'; return; }
    if (!p.ok) { blastOut.innerHTML = `<p class="text-sm text-error-600">${p.err ?? "השליחה נכשלה"}</p>`; return; }
    const skippedLine = (p.skipped ?? 0) > 0
      ? `\nלא יישלח ל-${p.skipped}:${p.optedOut ?? 0} ברשימת ההסרה, ${p.noConsent ?? 0} בלי הסכמה לדיוור.`
      : "";
    if ((p.willSend ?? 0) === 0) {
      alert(`אין נמענים שמותר לשלוח להם הודעה שיווקית.${skippedLine}`);
      return;
    }
    if (!confirm(`לשלוח את ההודעה ל-${p.willSend} נמענים?${skippedLine}`)) return;

    btn.disabled = true;
    blastOut.innerHTML = `<p class="text-sm text-gray-500">שולח ל-${p.willSend} נמענים…</p>`;
    const r = await post<BlastReply>("/Reports/SendBulk", payload);
    btn.disabled = false;

    if (!r) { blastOut.innerHTML = '<p class="text-sm text-error-600">שגיאת רשת</p>'; return; }
    if (!r.ok) { blastOut.innerHTML = `<p class="text-sm text-error-600">${r.err ?? "השליחה נכשלה"}</p>`; return; }

    const lines = (r.results ?? []).map((x) =>
      `<div class="flex items-center gap-2 border-b border-gray-100 px-1 py-0.5 text-xs ${x.ok ? "text-success-700" : "text-error-600"}">
         <span>${x.ok ? "✔" : x.skipped ? "⊘" : "✖"}</span><span class="font-medium">${esc(x.name)}</span>
         <span dir="ltr">${esc(x.mobile)}</span><span class="ms-auto text-gray-400">${esc(x.info)}</span>
       </div>`).join("");
    blastOut.innerHTML =
      `<p class="mb-2 text-sm font-semibold">נשלחו ${r.sent} · נכשלו ${r.failed}${(r.skipped ?? 0) > 0 ? ` · דולגו ${r.skipped}` : ""}</p>
       <div class="max-h-64 overflow-y-auto rounded-lg border border-gray-200">${lines}</div>`;
  });

  // ================= compare against a pasted Odyssea order list =================
  $<HTMLButtonElement>("[data-compare-run]")?.addEventListener("click", () => {
    const out = $<HTMLElement>("[data-compare-out]");
    const ta = $<HTMLTextAreaElement>("[data-compare-input]");
    if (!out || !ta) return;

    // Any run of digits in the pasted text is an order number — the Odyssea list comes in many
    // shapes (one per line, tab-separated, copied out of Excel), so parse leniently.
    const pasted = Array.from(new Set((ta.value.match(/\d{2,}/g) ?? [])));
    if (pasted.length === 0) { out.innerHTML = '<p class="text-sm text-gray-500">לא נמצאו מספרי הזמנה בטקסט שהודבק.</p>'; return; }

    const inTable = new Set(rows().map((r) => (r.dataset.order ?? "").trim()).filter((v) => v !== ""));
    const missingHere = pasted.filter((p) => !inTable.has(p));
    const missingThere = Array.from(inTable).filter((t) => !pasted.includes(t));

    const list = (title: string, items: string[], tone: string) =>
      `<div class="mb-3">
         <p class="mb-1 text-sm font-semibold ${tone}">${title} (${items.length})</p>
         ${items.length === 0
           ? '<p class="text-xs text-gray-400">—</p>'
           : `<div dir="ltr" class="max-h-40 overflow-y-auto rounded-lg border border-gray-200 p-2 text-start text-xs">${items.map(esc).join(", ")}</div>`}
       </div>`;

    out.innerHTML =
      `<p class="mb-2 text-xs text-gray-500">הודבקו ${pasted.length} מספרי הזמנה · בטבלה ${inTable.size}</p>` +
      list("ברשימה שהודבקה אך לא בטבלה", missingHere, "text-error-700") +
      list("בטבלה אך לא ברשימה שהודבקה", missingThere, "text-warning-700");
  });

  function esc(s: string): string {
    const d = document.createElement("span");
    d.textContent = s ?? "";
    return d.innerHTML;
  }

  applySearch();
  refreshSelection();
}
